<template>
  <div :id="computedId">
    <GenericFormModal
      :title="title"
      :action="action"
      :canSubmit="canSubmitData"
      :cancelable="true"
      :isShown="isShown"
      :actionId="softwareId"
      :showButtons="!loading"
      :id="computedId + '-modal'"
      v-on:submited="onSubmitedData"
      v-on:canceled="cancelled"
    >
      <Loading v-if="loading"></Loading>
      <div v-else>
        <InputTextName
          :label="'sections.softwareFormSection.softwareName' | i18n"
          identifier="nom_application"
          icon="microsoft icon"
          :initialValue="
            dataSoftware == undefined ? '' : dataSoftware.nom_application
          "
          :forcedValidations="nameForcedValidations"
          @onChange="
            data => {
              isNameValid = data.validation.isValid;
            }
          "
        />
        <InputSelect
          identifier="serveur_id"
          :selectMessage="'sections.softwareFormSection.serverName' | i18n"
          :options="servers"
          :isSubmitted="submitted"
          :selectedOption="serverId"
          @onChange="
            data => {
              serverSelectedIsValid = data.isValid;
            }
          "
        />
        <InputText
          :validations="computedUrlValidations"
          :label="'sections.softwareFormSection.procedureURL' | i18n"
          icon="clipboard icon"
          identifier="url_procedure"
          allowedChars="\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=."
          class="mb-5"
          :initialValue="dataSoftware.url_procedure || ''"
          :forcedValidations="urlForcedValidations"
          @onChange="
            data => {
              isUrlValid = data.validation.isValid;
            }
          "
        />
        <div>
          <h5 class="mt-5">
            {{ "sections.softwareFormSection.notifications" | i18n }}
          </h5>
          <InputCheckbox
            class="mr-5 d-inline-block"
            name="notification_email"
            :message="'sections.softwareFormSection.email' | i18n"
            :isChecked="dataSoftware.notification_email || false"
          />
          <InputCheckbox
            class="d-inline-block"
            name="notification_texto"
            :message="'sections.softwareFormSection.SMS' | i18n"
            :isChecked="dataSoftware.notification_texto || false"
          />
        </div>
        <InputCheckbox
          name="affichage"
          :message="'sections.softwareFormSection.dashboardDisplay' | i18n"
          isSwitch
          :isChecked="dataSoftware.affichage || false"
          class="mt-5 ml-1"
        />
        <!-- Champ invisible pour l'API. La valeur ne sera jamais utilisée, mais elle est nécessaire. -->
        <input type="hidden" name="categorie" value="software" />
      </div>
    </GenericFormModal>
    <b-toast
      :id="computedToastId"
      toast-class="bg-danger text-white text-center"
      :no-close-button="true"
    >
      <template v-slot:default>
        <i class="exclamation circle icon mr-3"></i>
        <span>{{ "sections.softwareFormSection.failure" | i18n }}</span>
      </template>
    </b-toast>
  </div>
</template>
<script>
import GenericFormModal from "@/components/generics/GenericFormModal.vue";
import InputTextName from "@/components/inputs/InputTextName.vue";
import InputCheckbox from "@/components/inputs/InputCheckbox.vue";
import InputSelect from "@/components/inputs/InputSelect.vue";
import InputText from "@/components/inputs/InputText.vue";
import Loading from "@/components/others/Loading.vue";
/**
 * @description Section pour l'ajout d'un logiciel
 * @author Gabriel Bertrand
 * @author Kevin Dubé
 * @author Benjamin Caron
 * @author Alexandre Gallant
 * @vue-prop {String} title - Le titre du formulaire
 * @vue-prop {Function} action - La fonction que le formulaire va exécuter
 * @vue-prop {Number} softwareId - L'id du logiciel lors d'une modification
 * @vue-prop {Boolean} isShown - Si la boîte modale s'affiche
 * @vue-prop {String} clientId - L'id du client avec lequel lier le logiciel
 * @vue-data {Number} serverSelected - Le serveur choisi dans la liste
 * @vue-data {Array} servers - Liste des serveurs
 * @vue-data {Boolean} submitted - Si le formulaire a été soumis ou non
 * @vue-data {Boolean} loading - True si le formulaire est en train de charger
 * @vue-data {Object} dataSoftware - Les informations du logiciel lors d'une modification
 * @vue-data {String} serverId - L'id du serveur du logiciel
 * @vue-data {Boolean} isNameValid - Si le nom du logiciel est valide
 * @vue-data {Boolean} isUrlValid - Si l'url est valide
 * @vue-data {Number} nameForcedValidations - Nombre de fois que les validations ont été forcées pour «name»
 * @vue-data {Number} urlForcedValidations - Nombre de fois que les validations ont été forcées pour «url»
 * @vue-computed {Array} computedUrlValidations - L'objet de validation à utiliser pour valider l'url
 * @vue-computed {String} computedId - L'id à donner au modal
 * @vue-computed {String} computedToastId - L'id du toast
 * @vue-event {void} canceled - Est émis lorsque le formulaire est annulé
 * @vue-event {void} successed-request - Est émis lorsque le résultat de la requête est positif (200)
 */
export default {
  name: "FormSoftwareSection",
  components: {
    InputTextName,
    InputText,
    InputCheckbox,
    InputSelect,
    GenericFormModal,
    Loading
  },
  props: {
    title: {
      type: String,
      required: true
    },
    action: {
      type: Function,
      required: true
    },
    softwareId: {
      type: Number,
      required: false
    },
    isShown: {
      type: Boolean,
      required: true
    },
    clientId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      serverSelected: null,
      servers: [],
      submitted: false,
      loading: false,
      dataSoftware: {},
      serverId: undefined,

      isNameValid: false,
      serverSelectedIsValid: false,
      isUrlValid: false,

      nameForcedValidations: 0,
      urlForcedValidations: 0
    };
  },
  watch: {
    /**
     * @description Effectue les requêtes nécéssaires selon le cas
     * @param {Boolean} newValue La nouvelle valeur de «isShown»
     */
    isShown: async function(newValue) {
      if (!newValue) {
        if (this.softwareId) {
          this.loading = false;
        }
        return;
      }
      if (!this.softwareId) {
        return;
      }
      await this.getDatas();
      this.isNameValid = true;
      this.serverSelectedIsValid = true;
      this.isUrlValid = true;
    }
  },
  computed: {
    computedUrlValidations() {
      return [
        {
          error: this.$i18n.t("errors.empty", {
            name: this.$i18n.t("sections.softwareFormSection.procedureURL")
          }),
          exec: v => v.trim() !== ""
        }
      ];
    },
    computedId() {
      return "software-" + (this.softwareId ? this.softwareId : "new");
    },
    computedToastId() {
      return (
        "error-toast-software-" + (this.softwareId ? this.softwareId : "new")
      );
    }
  },
  methods: {
    /**
     * @description Méthode qui gère l'annulation du formulaire
     */
    cancelled() {
      this.$emit("canceled");
    },
    /**
     * @description Gère les opérations lorsque le formulaire a été envoyé
     * @param {Object} res Le résultat du submit
     */
    async onSubmitedData(res) {
      res.status == 200
        ? this.$emit("successed-request")
        : this.$bvToast.show(this.computedToastId);
    },
    /**
     * @description Méthode qui récupère les serveurs de l'api avant le chargement de la section
     */
    async getServers() {
      try {
        let res = await this.$api.getServersByClient(this.clientId);
        let data = res.data.data;
        this.servers = [];
        for (let i = 0; i <= data.length - 1; i++) {
          this.servers.push({
            value: data[i].id,
            text: data[i].nom
          });

          if (this.softwareId && data[i].id == this.dataSoftware.serveur_id) {
            this.serverId = data[i].id.toString();
            this.serverSelected = data[i].id.toString();
          }
        }
      } catch (e) {
        this.servers = [{ value: null, text: "aucun serveur disponible" }];
      }
    },
    /**
     * @description Récupère le logiciel à modifier auprès de l'api
     */
    async getSoftware() {
      if (this.softwareId) {
        await this.$api
          .getSoftware(this.softwareId)
          .then(res => {
            this.dataSoftware = res.data.data;
            this.procedureUrl = res.data.data.url_procedure;
            this.softwareName = res.data.data.nom_application;
          })
          .catch();
      }
    },
    /**
     * @description Indique si l'information à envoyer est valide
     * @returns {Boolean} Vrai si les deux champs sont valides
     */
    canSubmitData() {
      this.submitted = true;
      const canSubmit =
        this.isNameValid && this.serverSelectedIsValid && this.isUrlValid;
      if (!canSubmit) {
        ++this.nameForcedValidations;
        ++this.urlForcedValidations;
      }
      return canSubmit;
    },
    /**
     * @description Récupère les informations nécéssaires pour afficher le formulaire
     */
    async getDatas() {
      this.loading = true;
      await this.getSoftware();
      await this.getServers();
      this.loading = false;
    }
  },
  /**
   * @description Fonction qui récupère les données dont le formulaire a besoin
   */
  beforeMount() {
    this.getDatas();
  }
};
</script>
<style scoped lang="scss">
.custom-checkbox .custom-control-label::before {
  border-radius: 3px !important;
}

InputTextName {
  margin-bottom: 0.5rem;
  margin-top: 3rem;
}

InputSelect {
  margin-bottom: 0.5rem;
}
</style>
